/**
 * Lightweight entity-resolution layer for the Defense Technology
 * Intelligence graph.
 *
 * Normalizes entity names before graph construction so the same
 * organization discovered in both the Publications API and the PLA
 * Contracts API resolves to ONE graph node — this is the key fusion point
 * between the two datasets.
 *
 * Normalization is deliberately conservative: whitespace/unicode/punctuation
 * cleanup plus a small curated alias table. Entities are NOT aggressively
 * merged on string similarity; raw source labels are preserved as aliases so
 * analysts can inspect why records were joined.
 */
import { IntelNodeType } from '../../types/intelligenceGraph';

/**
 * Curated alias table mapping known alternative names to a canonical
 * organization name. Keys are compared after `normalizeEntityName`.
 */
const ORGANIZATION_ALIASES: Record<string, string> = {
  // CETC 14th Research Institute — common variants
  'cetc 14th research institute': '中国电子科技集团公司第十四研究所',
  'cetc no.14 research institute': '中国电子科技集团公司第十四研究所',
  'nanjing research institute of electronics technology':
    '中国电子科技集团公司第十四研究所',
  中国电科十四所: '中国电子科技集团公司第十四研究所',
  中国电子科技集团第十四研究所: '中国电子科技集团公司第十四研究所',
  中国电子科技集团公司第14研究所: '中国电子科技集团公司第十四研究所',
  南京电子技术研究所: '中国电子科技集团公司第十四研究所',
};

/** Chinese/fullwidth punctuation mapped to halfwidth equivalents. */
const PUNCTUATION_MAP: Record<string, string> = {
  '（': '(',
  '）': ')',
  '，': ',',
  '。': '.',
  '、': ',',
  '：': ':',
  '；': ';',
  '“': '"',
  '”': '"',
  '‘': "'",
  '’': "'",
  '\u3000': ' ',
};

/** Zero-width and BOM characters that hide inside scraped entity names. */
const ZERO_WIDTH_RE = /[\u200B-\u200D\uFEFF\u2060]/g;

/**
 * Normalizes an entity name: NFKC unicode normalization, zero-width char
 * removal, Chinese punctuation folding, whitespace collapsing and trimming.
 */
export function normalizeEntityName(raw: string): string {
  let value = raw.normalize('NFKC').replace(ZERO_WIDTH_RE, '');
  value = value.replace(
    /[（）\u3000，。、：；“”‘’]/g,
    (ch) => PUNCTUATION_MAP[ch] ?? ch,
  );
  return value.replace(/\s+/g, ' ').trim();
}

/**
 * Normalizes PLA unit variations such as `中国人民解放军93145部队`,
 * `解放军93145部队` and `93145部队` to a single canonical form
 * (`中国人民解放军93145部队`) so numbered-unit records fuse correctly.
 */
function normalizePlaUnit(name: string): string {
  const unitMatch = name.match(/^(?:中国人民解放军|解放军)?\s*(\d{4,5})部队$/);
  if (unitMatch) {
    return `中国人民解放军${unitMatch[1]}部队`;
  }
  return name;
}

/**
 * Resolves a raw entity name to its canonical form: normalization, PLA unit
 * folding, then curated alias lookup (case-insensitive for Latin variants).
 */
export function resolveCanonicalName(raw: string): string {
  const normalized = normalizePlaUnit(normalizeEntityName(raw));
  const aliasKey = normalized.toLowerCase();
  return ORGANIZATION_ALIASES[aliasKey] ?? normalized;
}

/** Detects PLA/military organization names by well-known markers. */
export function looksLikePlaOrganization(name: string): boolean {
  return (
    /中国人民解放军|解放军|\d{4,5}部队|军事科学院|国防科技大学|国防大学|武警|人民武装警察/.test(
      name,
    ) || /\bPLA\b|People's Liberation Army/i.test(name)
  );
}

/**
 * Builds the stable normalized node id for an entity, e.g.
 * `org:中国电子科技集团公司第十四研究所` or `person:王侃`.
 *
 * Organizations, PLA orgs, buyers, suppliers and agencies share the `org:` id
 * namespace: they are all organizations playing different roles, and sharing
 * the namespace is what lets the same company observed as a publication
 * affiliation and as a procurement supplier resolve to one node.
 */
export function makeNodeId(type: IntelNodeType, name: string): string {
  const canonical = resolveCanonicalName(name);
  switch (type) {
    case 'organization':
    case 'pla':
    case 'buyer':
    case 'supplier':
    case 'agency':
      return `org:${canonical}`;
    case 'person':
      return `person:${canonical}`;
    case 'technology':
      return `technology:${canonical.toLowerCase()}`;
    case 'research':
      return `research:${name}`;
    case 'procurement':
      return `procurement:${name}`;
    case 'email':
      return `email:${canonical.toLowerCase()}`;
    case 'domain':
      return `domain:${canonical.toLowerCase()}`;
    default:
      return `${type}:${canonical}`;
  }
}

/**
 * Node-type precedence when the same organization is observed in multiple
 * roles. PLA classification always wins (risk intelligence), then the
 * generic organization role, then supplier/agency roles.
 */
const ORG_TYPE_PRECEDENCE: IntelNodeType[] = [
  'pla',
  'organization',
  'buyer',
  'supplier',
  'agency',
];

/**
 * Picks the winning type when two observations of the same org-namespace
 * node disagree (e.g. seen as `supplier` in procurement and `organization`
 * in publications). Non-org types never conflict because their id
 * namespaces differ.
 */
export function mergeNodeTypes(
  a: IntelNodeType,
  b: IntelNodeType,
): IntelNodeType {
  if (a === b) return a;
  const ia = ORG_TYPE_PRECEDENCE.indexOf(a);
  const ib = ORG_TYPE_PRECEDENCE.indexOf(b);
  if (ia === -1 || ib === -1) return a;
  return ia <= ib ? a : b;
}
